import styles from './FJStrokeSet.module.less';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import FJPopupMenuArrow from '../arrow/FJPopupMenuArrow.jsx';
import FJSetLabel from '../FJSetLabel/FJSetLabel';
import FJLocalStore from '../../../store/FJLocalStore';
import FJSliderSet from '../FJSliderSet/FJSliderSet';
import FJColorAndOpacitySet from '../FJColorAndOpacitySet/FJColorAndOpacitySet';
import FJConstant from '../../../constants/FJConstant.js';
import { IMAGE_STROKE_CONFIG, STROKE_LIST, IMAGE_STROKE_LIMIT_CONFIG } from '@fj/fj-image-stroke';

const FJStrokeSet = (props, ref) => {
    const { selectedIndex, onStrokeInfoChange, style, strokeInfo, itemWidth = 100, space = 20 } = props;
    const {
        type: selectedStrokeType,
        color,
        opacity,
        width,
        offset,
        blur,
        direction,
        glowColor,
        glowOpacity,
        glowWidth,
        shadowColor,
        shadowOpacity,
    } = strokeInfo;

    const strokeConfig = IMAGE_STROKE_CONFIG[selectedStrokeType] || {};
    const strokeDefault = STROKE_LIST.find(item => item.type === selectedStrokeType) || {};
    const strokeLimitConfig = IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType] || {};

    const strokeWidth =
        width !== '' && strokeConfig.width
            ? Math.floor(
                  ((width - IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minWidth) /
                      (IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].maxWidth -
                          IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minWidth)) *
                      100,
              )
            : width;

    const strokeBlur =
        blur !== '' && strokeConfig.blur
            ? Math.floor(
                  ((blur - IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minBlur) /
                      (IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].maxBlur -
                          IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minBlur)) *
                      100,
              )
            : blur;

    const strokeOffset =
        offset !== '' && strokeConfig.offset
            ? Math.floor(
                  ((offset - IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minOffset) /
                      (IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].maxOffset -
                          IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minOffset)) *
                      100,
              )
            : offset;

    const arrowX = useMemo(() => {
        const pIndex = selectedIndex % 4;
        return (itemWidth + space) * pIndex + itemWidth / 2;
    }, [selectedIndex, itemWidth, space]);

    const _setRef = useRef(null);
    useImperativeHandle(ref, () => {
        return {
            getNode: () => {
                return _setRef.current;
            },
        };
    }, [_setRef.current]);

    const _onColorChange = (color, changeType = FJConstant.CHANGE_END) => {
        onStrokeInfoChange({ strokeInfo: { ...strokeInfo, color: color } }, changeType);
    };

    const _onGlowColorChange = (color, changeType = FJConstant.CHANGE_END) => {
        onStrokeInfoChange({ strokeInfo: { ...strokeInfo, glowColor: color } }, changeType);
    };

    const _onOpacityChange = (data, changeType = FJConstant.CHANGE_END) => {
        onStrokeInfoChange({ strokeInfo: { ...strokeInfo, opacity: data } }, changeType);
    };

    const _onGlowOpacityChange = (data, changeType = FJConstant.CHANGE_END) => {
        onStrokeInfoChange({ strokeInfo: { ...strokeInfo, glowOpacity: data } }, changeType);
    };

    const _onShadowOpacityChange = (data, changeType = FJConstant.CHANGE_END) => {
        onStrokeInfoChange({ strokeInfo: { ...strokeInfo, shadowOpacity: data } }, changeType);
    };

    const _onShadowColorChange = (color, changeType = FJConstant.CHANGE_END) => {
        onStrokeInfoChange({ strokeInfo: { ...strokeInfo, shadowColor: color } }, changeType);
    };

    const _onAttrChange = (value, attribute, changeType = FJConstant.CHANGE_END) => {
        const temp = { ...strokeInfo };
        if (attribute === 'width') {
            temp[attribute] =
                (value / 100) *
                    (IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].maxWidth -
                        IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minWidth) +
                IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minWidth;
        } else if (attribute === 'blur') {
            temp[attribute] =
                (value / 100) *
                    (IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].maxBlur -
                        IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minBlur) +
                IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minBlur;
        } else if (attribute === 'offset') {
            temp[attribute] =
                (value / 100) *
                    (IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].maxOffset -
                        IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minOffset) +
                IMAGE_STROKE_LIMIT_CONFIG[selectedStrokeType].minOffset;
        } else {
            temp[attribute] = Math.floor(value);
        }

        onStrokeInfoChange({ strokeInfo: { ...temp }, key: attribute }, changeType);
    };

    return (
        <div className={styles.box} ref={_setRef} style={style} data-auto-id='APP_stroke_set'>
            <FJPopupMenuArrow style={{ left: arrowX }} />
            {strokeConfig.color && (
                <div className={styles.color_box}>
                    <FJSetLabel
                        text={FJLocalStore._(
                            selectedStrokeType === 'glow-stroke' || selectedStrokeType === 'border-shadow-stroke'
                                ? 'border-color'
                                : 'color',
                        )}
                    >
                        <FJColorAndOpacitySet
                            color={color}
                            opacity={opacity}
                            hasOpacity={strokeConfig.opacity}
                            onColorChange={_onColorChange}
                            onOpacityChange={_onOpacityChange}
                        />
                    </FJSetLabel>

                    {strokeConfig.glowColor && (
                        <FJSetLabel text={FJLocalStore._('glow-color')}>
                            <FJColorAndOpacitySet
                                color={glowColor}
                                opacity={glowOpacity}
                                hasOpacity={true}
                                onColorChange={_onGlowColorChange}
                                onOpacityChange={_onGlowOpacityChange}
                            />
                        </FJSetLabel>
                    )}

                    {strokeConfig.shadowColor && (
                        <FJSetLabel text={FJLocalStore._('shadow-color')}>
                            <FJColorAndOpacitySet
                                color={shadowColor}
                                opacity={shadowOpacity}
                                hasOpacity={true}
                                onColorChange={_onShadowColorChange}
                                onOpacityChange={_onShadowOpacityChange}
                            />
                        </FJSetLabel>
                    )}
                </div>
            )}
            {strokeConfig.width && (
                <FJSliderSet
                    text={FJLocalStore._('width')}
                    name='width'
                    value={strokeWidth}
                    onChange={_onAttrChange}
                    defaultValue={strokeDefault.width}
                    min={0}
                    max={100}
                />
            )}
            {strokeConfig.glowWidth && (
                <FJSliderSet
                    text={FJLocalStore._('glow')}
                    name='glowWidth'
                    value={glowWidth}
                    onChange={_onAttrChange}
                    defaultValue={strokeDefault.glowWidth}
                    min={strokeLimitConfig.minGlowWidth}
                    max={strokeLimitConfig.maxGlowWidth}
                />
            )}
            {strokeConfig.blur && (
                <FJSliderSet
                    text={FJLocalStore._('blur')}
                    name='blur'
                    value={strokeBlur}
                    onChange={_onAttrChange}
                    defaultValue={strokeDefault.blur}
                    min={0}
                    max={100}
                />
            )}
            {strokeConfig.direction && (
                <FJSliderSet
                    text={FJLocalStore._('direction')}
                    name='direction'
                    value={direction}
                    onChange={_onAttrChange}
                    defaultValue={strokeDefault.direction}
                />
            )}
            {strokeConfig.offset && (
                <FJSliderSet
                    text={FJLocalStore._('offset')}
                    name='offset'
                    value={strokeOffset}
                    onChange={_onAttrChange}
                    defaultValue={strokeDefault.offset}
                    min={0}
                    max={100}
                />
            )}
        </div>
    );
};

export default forwardRef(FJStrokeSet);
